import { useMemo, useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Box, NotFound } from '@factorialco/deprecated-design-system'
import { OrgChartPageDocument } from 'generated/resources/graphql'

import { useCallback } from 'modules/core/hooks/useCallback'
// nosemgrep do_not_use_core_card
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import OrgChart from 'modules/org_chart/components/OrgChart'
import Provider from 'modules/org_chart/components/Provider'
import CollectionsBootstrap from 'modules/org_chart/lib/CollectionsBootstrap'
import {
  type GraphQLEmployee,
  getActiveEmployeesWithContract,
  isOrphan,
  peopleHierarchicallyManagedByEmployee,
} from 'modules/org_chart/lib/employeeUtils'
import TeamChartActivationBanner from 'modules/teams/components/TeamChartActivationBanner'

const Container = () => {
  const { company } = useCurrentSession()

  const { data, loading } = useQuery(OrgChartPageDocument, { variables: { companyId: company.id } })

  const containerRef = useRef<HTMLDivElement>(null)

  const svgRef = useRef<SVGSVGElement>(null)
  const [size, setSize] = useState<[height: number, width: number]>([0, 0])

  const activeEmployeesWithContract = useMemo(
    () => getActiveEmployeesWithContract(data?.employees?.employees ?? []),
    [data?.employees?.employees]
  )

  const employeeMap = useMemo(
    () => new Map(activeEmployeesWithContract.map((e) => [e.id, e])),
    [activeEmployeesWithContract]
  )

  const heads = useMemo(
    () => activeEmployeesWithContract.filter((x) => !x.managerId || isOrphan(x, employeeMap)),
    [activeEmployeesWithContract, employeeMap]
  )

  const traceableEmployees = useMemo(
    () =>
      heads.reduce<GraphQLEmployee[]>(
        (acc, x) => [
          ...acc,
          ...peopleHierarchicallyManagedByEmployee(x, activeEmployeesWithContract, employeeMap),
        ],
        heads
      ),
    [heads, activeEmployeesWithContract, employeeMap]
  )

  const canSeeOrgChart = data?.apiCore?._policies.accessReadOrgChart.can ?? false
  const canCreateTeams = data?.teams?._policies.teamCreate.can ?? false
  const canSeeLegalGender = data?.employees?._policies.seeEmployeeLegalGender.can ?? false
  const canSeeAnyScopedJobTitle = traceableEmployees.some(
    (employee) => employee.referenceContract?._policies?.seeJobTitle?.can ?? false
  )
  const canSeeJobTitle =
    (data?.contracts?._policies?.contractSeeJobTitle?.can ?? false) || canSeeAnyScopedJobTitle
  const canSeeJobCatalog = data?.jobCatalog?._policies?.seeJobCatalog?.can ?? false
  const canSeeJobCatalogTree = data?.jobCatalog?._policies?.jobCatalogTreeSee?.can ?? false
  const canExportOrgChart = data?.employees?._policies.exportOrgChart.can ?? false
  const canManagePositions = data?.positions?._policies.managePosition.can ?? false
  const canSeeJobPostings = data?.ats?._policies.useAts.can ?? false
  const canCreateJobPostings = data?.ats?._policies.createJobPostings.can ?? false

  const canExport =
    canExportOrgChart &&
    (data?.features?.featuresConnection.nodes.some(
      (el) => el.name === 'organization_chart_exports' && el.enabled
    ) ??
      false)

  const canSnapshot =
    data?.features?.featuresConnection.nodes.some(
      (el) => el.name === 'dev_orgchart_snapshot' && el.enabled
    ) ?? false

  const hasLegacyJobCatalog =
    data?.features?.featuresConnection.nodes.find(
      (el) => el.name === 'legacy_job_catalog' && el.enabled
    ) ?? false

  const isReady = !loading && size[1] > 0

  const handleResize = useCallback(() => {
    if (containerRef.current) {
      const { offsetWidth, offsetHeight } = containerRef.current
      setSize((prevSize) => {
        // Update if size changes
        if (prevSize[0] !== offsetWidth || prevSize[1] !== offsetHeight) {
          return [offsetWidth, offsetHeight]
        }
        return prevSize
      })
    }
  }, [])

  useUnsafeEffect(() => {
    window.addEventListener('resize', handleResize)
    track(trackingEvents.employee.page.orgChart.visited)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  useUnsafeEffect(() => handleResize())

  if (isReady && !canSeeOrgChart) {
    return (
      <NotFound
        title={i18n.t('errors.page_not_found.title')}
        description={i18n.t('errors.page_not_found.description')}
      />
    )
  }

  return (
    <ResponsivePage.Body variant="fullscreen" showHeader={false} error={null} loading={loading}>
      <Box padding="s12" height="full">
        {isReady && canCreateTeams && <TeamChartActivationBanner companyId={company.id} />}

        <Provider>
          <Box width="full" height="full" ref={containerRef}>
            {isReady && (
              <OrgChart
                heads={heads}
                employees={traceableEmployees}
                size={size}
                ref={svgRef}
                showLegalGenders={canSeeLegalGender}
                canExport={canExport}
                canSnapshot={canSnapshot}
                canManagePositions={canManagePositions}
                canSeeJobPostings={canSeeJobPostings}
                canCreateJobPostings={canCreateJobPostings}
                canSeeJobTitle={canSeeJobTitle}
                canSeeJobCatalog={canSeeJobCatalog}
                canSeeJobCatalogTree={canSeeJobCatalogTree}
                hasLegacyJobCatalog={!!hasLegacyJobCatalog}
                allEmployees={activeEmployeesWithContract}
                employeeMap={employeeMap}
              />
            )}
            <Outlet />
          </Box>
        </Provider>
      </Box>
    </ResponsivePage.Body>
  )
}

const OrgChartPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}
export default OrgChartPage
